import { BellAlertIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";

import RealTimeWrapper from "./real-time";
import clsx from "clsx";
import config from "config";
import { format } from "date-fns";
import { useUnreadNotificationMessages } from "hooks/data/use-unread-notification-messages";

const NotificationBanner: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { unreadNotificationMessages, loading, refetch } =
    useUnreadNotificationMessages();
  const [showBadge, setShowBadge] = useState(false);
  const [totalNotifications, setTotalNotifications] = useState(0);

  useEffect(() => {
    const total = getTotalThreadNotifications();
    setTotalNotifications(total);
    setShowBadge(total > 0);
  }, [unreadNotificationMessages]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const formatDate = (dateString: any) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "N/A";
    }
  };

  const getTotalThreadNotifications = () => {
    return unreadNotificationMessages.reduce((total, notification) => {
      return total + (notification?.thread_notification?.length || 0);
    }, 0);
  };

  const handleNotificationClick = (notification: any) => {
    const policyNumber = notification?.offer_detail?.policy_number || "";
    const encodedPolicyNumber = encodeURIComponent(policyNumber);
    const isOpenOrPending = ["OPEN", "PENDING"].includes(
      notification?.offer_status
    );
    const endpoint = isOpenOrPending ? "create-slip" : "create-closing";
    const searchParam = `?view=month&page=1&pageSize=10&fromDate=2025-03-01&toDate=2025-03-31&year=2025&search=${encodedPolicyNumber}`;

    const currentDomain = window.location.origin;
    const url = `${currentDomain}/${endpoint}${searchParam}`;

    window.location.href = url;
  };

  if (totalNotifications === 0 && !isExpanded) {
    return null;
  }

  return (
    <RealTimeWrapper channel={config.pusher.channels.placeIt} callBack={() => refetch()} events={["cedant.manager_onboarded", "cedant.placed_risk", "cedant.updated_risk", "cedant.uploaded_supporting_documents", "cedant.send_thread_notification", "broker.send_thread_notification", "broker.update_offer", "broker.accept_offer", "broker.declined_offer"]}>
      <div
        className={clsx(
          "fixed right-4 z-50 transition-all duration-300",
          isExpanded ? "bottom-4 w-96" : "bottom-16 w-16"
        )}
      >
        <div
          className={clsx(
            "rounded-lg shadow-lg flex flex-col transition-all duration-300",
            isExpanded
              ? "bg-card-500 h-auto max-h-96"
              : "animate-containerColor h-16 overflow-hidden"
          )}
        >
          <div
            className={clsx(
              "flex items-center justify-between border-b border-card-600",
              isExpanded ? "p-4" : "p-3"
            )}
          >
            <div className={clsx("flex items-center", !isExpanded && "mx-auto")}>
              <button
                onClick={toggleExpand}
                className="relative text-white hover:text-gray-300"
              >
                <BellAlertIcon
                  className={clsx(
                    "transition-all",
                    isExpanded ? "h-7 w-7" : "h-9 w-9",
                    showBadge && !isExpanded && "animate-ring origin-top"
                  )}
                />
                {showBadge && !isExpanded && (
                  <span className="absolute -top-2 -right-2 bg-destructive-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg border border-white">
                    {totalNotifications > 9 ? "9+" : totalNotifications}
                  </span>
                )}
              </button>
              {isExpanded && (
                <span className="ml-2 text-white text-base font-medium">
                  PlaceIt Notifications ({totalNotifications})
                </span>
              )}
            </div>
            {isExpanded && (
              <button
                onClick={toggleExpand}
                className="text-white hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>

          {isExpanded && (
            <div className="overflow-y-auto flex-grow">
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-white"></div>
                </div>
              ) : unreadNotificationMessages.length === 0 ? (
                <div className="p-4 text-center text-gray-300 text-sm">
                  No unread notifications
                </div>
              ) : (
                <ul className="divide-y divide-card-600">
                  {unreadNotificationMessages.map((notification, index) => (
                    <li
                      key={index}
                      className="p-4 hover:bg-card-600 cursor-pointer"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex flex-col">
                        <div className="flex justify-between items-start">
                          <span className="text-sm font-medium text-white">
                            {notification?.offer_detail?.policy_number ||
                              "Unknown Insurer"}
                            , {notification?.offer_detail?.insured_by || "N/A"}
                            {notification?.thread_notification &&
                              notification.thread_notification.length > 0 && (
                                <span className="ml-2 bg-destructive-500 text-white text-xs rounded-full px-2 py-0.5">
                                  {notification.thread_notification.length}
                                </span>
                              )}
                          </span>
                          <span
                            className={clsx(
                              "text-xs px-2 py-1 rounded",
                              notification?.offer_status === "PENDING"
                                ? "bg-yellow-600 text-yellow-100"
                                : notification?.offer_status === "OPEN"
                                  ? "bg-green-600 text-green-100"
                                  : "bg-red-600 text-green-100"
                            )}
                          >
                            {notification?.offer_status}
                          </span>
                        </div>

                        <div className="mt-1 text-xs text-gray-300">
                          {notification?.classofbusiness?.business_name || "N/A"}
                        </div>
                        <div className="mt-1 text-xs text-gray-300">
                          {notification?.insurer?.insurer_company_name || "N/A"}
                        </div>

                        <div className="mt-1 flex justify-between text-xs text-gray-400">
                          <span>
                            {notification?.offer_detail?.period_of_insurance_from
                              ? notification.offer_detail.period_of_insurance_from
                              : "N/A"}{" "}
                            -{" "}
                            {notification?.offer_detail?.period_of_insurance_to
                              ? notification.offer_detail.period_of_insurance_to
                              : "N/A"}
                          </span>
                          <span
                            className={clsx(
                              "text-xs font-medium",
                              notification?.payment_status === "PAID"
                                ? "text-green-400"
                                : "text-yellow-400"
                            )}
                          >
                            {notification?.payment_status}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </RealTimeWrapper>
  );
};

export default NotificationBanner;
